import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { backendService } from "../services/backend.service";
import type { UpdateUserSettingsRequest, UserSettings } from "../utils/models";
import { toOptimisticUserSettingsEntries } from "../utils/userSettingsCacheKeys";
import { isHeadNurse } from "../utils/utilFunctions";

/** Single source of truth for the user-settings query key, so the query, its mutations, and
 *  invalidations can never drift apart. `all` matches every department's cache for broad invalidation. */
export const userSettingsKeys = {
	all: ["userSettings"] as const,
	department: (departmentId: string, userId: string) => ["userSettings", departmentId, userId] as const,
	/** Per-user entry for staff `useUserSettings` doesn't load for, so their colour choices stay isolated across logins. */
	readOnly: (departmentId: string, userId: string) => ["userSettings", departmentId, "read-only", userId] as const,
} as const;

const userSettingsQueryOptions = (departmentId: string, userId: string) => ({
	queryKey: userSettingsKeys.department(departmentId, userId),
	queryFn: async (): Promise<UserSettings> => {
		const response = await backendService.getUserSettings(departmentId);
		return response.data;
	},
	staleTime: Number.POSITIVE_INFINITY,
	refetchOnWindowFocus: false,
});

export const useUserSettings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();

	return useQuery<UserSettings>({
		...userSettingsQueryOptions(chosenDepartment.department_id, userInfo.id),
		enabled: !!chosenDepartment.department_id && !!userInfo.id && isHeadNurse(userInfo.role),
	});
};

/** Settings available to any department user; head nurses share the editable settings cache. */
export const useAccessibleUserSettings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const departmentId = chosenDepartment.department_id;
	return useQuery<UserSettings>({
		...userSettingsQueryOptions(departmentId, userInfo.id),
		queryKey: isHeadNurse(userInfo.role) ? userSettingsKeys.department(departmentId, userInfo.id) : userSettingsKeys.readOnly(departmentId, userInfo.id),
		enabled: !!departmentId && !!userInfo.id,
	});
};

/**
 * The job colours this user sees: a head nurse's own settings (so a save shows at once), and for everyone else
 * a read of what the settings endpoint gives them — their department's default unless they saved their own.
 * `undefined` until loaded.
 */
export const useJobColorPins = (): Record<string, number> | undefined => {
	return useAccessibleUserSettings().data?.job_colors;
};

export const useRoleColorPins = (): Record<string, number> | undefined => useAccessibleUserSettings().data?.role_colors;

/**
 * Asks the server again for this user's settings, for a window that opens on them (the Day printout).
 * They may have changed elsewhere since they loaded — a support user's Save as default rewrites everyone's
 * Printout 1 — and this query never refetches by itself (`staleTime: Infinity`).
 *
 * Never over this user's own writes: skipped while any write is running or `hasUnsavedEdit()` says an edit
 * is waiting, and the answer is thrown away if either became true, or the cached copy changed, while it was
 * on its way. Any running mutation counts, not only settings writes: those carry no key to tell them apart,
 * and skipping a refresh only leaves what is already on screen. Resolves whether the cached settings were
 * replaced — an unchanged answer keeps the cached object (structural sharing), so nothing re-renders.
 */
export const useRefreshUserSettings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();
	const { trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo.id;

	return useCallback(
		async (hasUnsavedEdit: () => boolean): Promise<boolean> => {
			const queryKey = userSettingsKeys.department(departmentId, userId);
			const before = queryClient.getQueryData<UserSettings>(queryKey);
			const isBusy = (): boolean => hasUnsavedEdit() || queryClient.isMutating() > 0;
			// Not loaded yet: the query's own first load brings the server's copy.
			if (before === undefined || isBusy()) return false;
			let fresh: UserSettings;
			try {
				fresh = (await backendService.getUserSettings(departmentId)).data;
			} catch (error) {
				// The cached settings stay on screen; the failure is only worth knowing about, not interrupting for.
				trackError("user_settings_refresh_on_open", { department_id: departmentId, error_message: error instanceof Error ? error.message : String(error) });
				return false;
			}
			if (isBusy() || queryClient.getQueryData<UserSettings>(queryKey) !== before) return false;
			queryClient.setQueryData<UserSettings>(queryKey, fresh);
			return queryClient.getQueryData<UserSettings>(queryKey) !== before;
		},
		[queryClient, trackError, departmentId, userId],
	);
};

export const useUpdateUserSettings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();
	const { trackError } = useAnalytics();
	const queryKey = isHeadNurse(userInfo.role)
		? userSettingsKeys.department(chosenDepartment.department_id, userInfo.id)
		: userSettingsKeys.readOnly(chosenDepartment.department_id, userInfo.id);

	return useMutation({
		mutationFn: async (settings: UpdateUserSettingsRequest) => {
			const response = await backendService.updateUserSettings(chosenDepartment.department_id, settings);
			return response.data;
		},
		onMutate: async (newSettings: UpdateUserSettingsRequest) => {
			await queryClient.cancelQueries({ queryKey });

			const previousSettings = queryClient.getQueryData<UserSettings>(queryKey);

			if (previousSettings) {
				queryClient.setQueryData<UserSettings>(queryKey, {
					...previousSettings,
					...toOptimisticUserSettingsEntries(newSettings),
				});
			}

			return { previousSettings };
		},
		onError: (error, _newSettings, context) => {
			if (context?.previousSettings) {
				queryClient.setQueryData<UserSettings>(queryKey, context.previousSettings);
			}
			trackError("update_user_settings", { error_message: error?.message });
		},
		onSuccess: (data, variables) => {
			// Update the cache with the server response (ensures consistency)
			queryClient.setQueryData<UserSettings>(queryKey, data);
			if (variables.saveAsDefault) queryClient.invalidateQueries({ queryKey: userSettingsKeys.all });
		},
	});
};

/**
 * Reset only the given (snake_case) columns on the user's row to the department default.
 *
 * `options.includePrintoutPresets` (the printout popup's "Use department printouts") also replaces the
 * user's saved printouts with the department's and puts them back on Printout 1. Every other caller
 * passes nothing and sends exactly the request it always did.
 */
export const useRestoreUserSettings = (options?: { includePrintoutPresets: boolean }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();
	const { trackError } = useAnalytics();
	const queryKey = isHeadNurse(userInfo.role)
		? userSettingsKeys.department(chosenDepartment.department_id, userInfo.id)
		: userSettingsKeys.readOnly(chosenDepartment.department_id, userInfo.id);

	return useMutation({
		mutationFn: async (fields: string[]) => {
			const response = await backendService.restoreUserSettings(chosenDepartment.department_id, fields, options);
			return response.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData<UserSettings>(queryKey, data);
		},
		onError: (error) => {
			trackError("restore_user_settings", { error_message: error?.message });
		},
	});
};

export const useUpdateNotificationStatus = () => {
	const queryClient = useQueryClient();
	const { trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (status: string) => {
			const response = await backendService.updateNotificationsStatus(status);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userSettingsKeys.all });
		},
		onError: (error) => {
			trackError("update_notification_status", { error_message: error?.message });
		},
	});
};
